import React, { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ExamRunner, createStudentAdapter } from "../ExamRunner";

/**
 * TakeAssessmentPage — the enrolled student's exam.
 *
 * This used to be 630 lines mixing transport, anti-cheat wiring, state and
 * presentation. It is now a thin mount of the shared ExamRunner with the
 * student adapter, so students and public exam candidates get the same screen
 * and every fix lands in both.
 *
 * Behaviour carried over verbatim (parity checklist from
 * docs/public-exams/EXAM_RUNNER_UX_SPEC.md):
 *   sections + per-section metadata · five-state question palette · mark for
 *   review · clear response · mark & next · save & next · server-derived timer
 *   with auto-submit at zero · fullscreen gate · tab-switch detection with
 *   auto-submit at the configured threshold · right-click and copy/paste
 *   blocking · MathText, images and code snippets · single/multi/true-false/
 *   text/fill-blank/NAT inputs · redirect to the result page on submit.
 *
 * Gained: retrying autosave with a visible save state (the old screen swallowed
 * every failed save), offline recovery, a real submit-review screen, keyboard
 * navigation, font sizing, and a palette that works on a phone.
 */
const TakeAssessmentPage = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const adapter = useMemo(() => createStudentAdapter({ attemptId }), [attemptId]);

  const handleFinished = useCallback(() => {
    navigate(`/s/assessment/result/${attemptId}`);
  }, [navigate, attemptId]);

  return (
    <ExamRunner
      adapter={adapter}
      onFinished={handleFinished}
    />
  );
};

export default TakeAssessmentPage;
